import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { carsApi, imageUrl } from "../../lib/api";
import { formatKm, formatPrice } from "../../lib/format";
import { Edit3, Trash2, CheckCircle2, Plus, Search, Languages, Loader2, Copy, Share2, Download, ClipboardCopy, X as XIcon, Sparkles, Tag, Check, Eye, EyeOff, RefreshCw } from "lucide-react";
import { API_BASE } from "../../lib/api";
import axios from "axios";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";
import { useNavigate } from "react-router-dom";

export default function AdminCars() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [translating, setTranslating] = useState(false);
  const [reformatting, setReformatting] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [shareCar, setShareCar] = useState(null);
  const [soldCar, setSoldCar] = useState(null);
  const [priceEditId, setPriceEditId] = useState(null);
  const [priceDraft, setPriceDraft] = useState("");
  const [savingPriceId, setSavingPriceId] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await carsApi.list({ upcoming: "all", hidden: "all" });
      setCars(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API_BASE}/admin/as24-sync/run`, {}, { withCredentials: true });
      if (data?.status === "already_running") {
        toast({ title: "Sync loopt al", description: "Even geduld tot de huidige sync klaar is." });
      } else {
        toast({ title: "Sync gestart", description: "Nieuwe wagens en prijzen worden opgehaald…" });
      }
      const poll = async () => {
        try {
          const { data: st } = await axios.get(`${API_BASE}/admin/as24-sync/status`, { withCredentials: true });
          const l = st?.latest;
          if (l && l.status !== "running") {
            setSyncing(false);
            if (l.status === "done") {
              toast({ title: "Sync voltooid", description: `${l.added || 0} toegevoegd · ${l.price_updated || 0} prijswijziging(en)` });
              load();
            } else if (l.status === "error") {
              toast({ title: "Sync mislukt", description: l.error || "Onbekende fout" });
            }
            return;
          }
          setTimeout(poll, 3000);
        } catch {
          setSyncing(false);
        }
      };
      setTimeout(poll, 2500);
    } catch (e) {
      setSyncing(false);
      toast({ title: "Sync starten mislukt", description: e?.response?.data?.detail || String(e) });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Auto definitief verwijderen?")) return;
    await carsApi.remove(id);
    toast({ title: "Verwijderd", description: "Auto is verwijderd uit stock." });
    load();
  };

  const handleSold = (id) => {
    const car = cars.find((c) => c.id === id);
    setSoldCar(car || { id });
  };

  const handleToggleHidden = async (c) => {
    const next = !c.hidden;
    try {
      await carsApi.updateVisibility(c.id, next);
      setCars((prev) => prev.map((x) => (x.id === c.id ? { ...x, hidden: next } : x)));
      toast({
        title: next ? "Verborgen op website" : "Terug zichtbaar",
        description: next
          ? `"${c.title}" is niet meer zichtbaar op de website.`
          : `"${c.title}" is terug zichtbaar op de website.`,
      });
    } catch (e) {
      toast({ title: "Aanpassing mislukt", description: e?.response?.data?.detail || String(e) });
    }
  };

  const handleDuplicate = async (id) => {
    setDuplicatingId(id);
    try {
      const src = await carsApi.get(id);
      // Strip server-managed + import-tracking fields, prepend "[Kopie] " to the title.
      const {
        id: _id,
        created_at: _ca,
        as24_guid: _g,
        import_source: _is,
        ...payload
      } = src;
      payload.title = `[Kopie] ${src.title}`.slice(0, 200);
      const created = await carsApi.create(payload);
      toast({
        title: "Gedupliceerd",
        description: `"${src.title}" gekopieerd. Pas de kopie aan en sla op.`,
      });
      navigate(`/admin/cars/${created.id}/edit`);
    } catch (e) {
      toast({
        title: "Duplicatie mislukt",
        description: e?.response?.data?.detail || String(e),
        variant: "destructive",
      });
    } finally {
      setDuplicatingId(null);
    }
  };

  // Poll an async admin job until done/error or the user navigates away.
  // Returns the final job document.
  const pollJob = async (jobId, label) => {
    while (true) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const job = await carsApi.getJob(jobId);
        if (job.status === "done" || job.status === "error") return job;
        // Optional intermediate progress UI is omitted to keep the toast quiet
        if (job.total) {
          console.debug(`[${label}] ${job.processed || 0}/${job.total}`);
        }
      } catch {
        // transient — retry
      }
    }
  };

  const handleTranslateAll = async () => {
    if (!window.confirm("Alle wagens vertalen naar FR + EN? Wordt op de achtergrond uitgevoerd. Reeds vertaalde wagens worden overgeslagen.")) return;
    setTranslating(true);
    try {
      const { job_id } = await carsApi.translateAll();
      toast({ title: "Vertaling gestart", description: "Achtergrond-job draait. Even geduld..." });
      const final = await pollJob(job_id, "translate-all");
      if (final.status === "done") {
        toast({
          title: "Vertaling klaar",
          description: `${final.translated || 0} vertaald · ${final.skipped || 0} overgeslagen · ${final.failed || 0} mislukt (totaal ${final.total || 0})`,
        });
        load();
      } else {
        toast({ title: "Vertaling mislukt", description: final.error || "Onbekende fout" });
      }
    } catch (e) {
      toast({ title: "Vertaling mislukt", description: e?.response?.data?.detail || String(e) });
    } finally {
      setTranslating(false);
    }
  };

  const handleReformatAll = async () => {
    if (!window.confirm("Alle beschrijvingen herformatteren (regex + Gemini → intro / bullets / outro) en opnieuw vertalen naar FR + EN? Wordt op de achtergrond uitgevoerd.")) return;
    setReformatting(true);
    try {
      const { job_id } = await carsApi.cleanDescriptions();
      toast({ title: "Herformatteren gestart", description: "Achtergrond-job draait. Even geduld..." });
      const final = await pollJob(job_id, "clean-descriptions");
      if (final.status === "done") {
        toast({
          title: "Herformatteren klaar",
          description: `${final.cleaned || 0} opgeschoond · ${final.retranslated || 0} hervertaald · ${final.failed || 0} mislukt (totaal ${final.total || 0})`,
        });
        load();
      } else {
        toast({ title: "Herformatteren mislukt", description: final.error || "Onbekende fout" });
      }
    } catch (e) {
      toast({ title: "Herformatteren mislukt", description: e?.response?.data?.detail || String(e) });
    } finally {
      setReformatting(false);
    }
  };

  const startPriceEdit = (c) => {
    setPriceEditId(c.id);
    setPriceDraft(c.price == null ? "" : String(c.price));
  };

  const cancelPriceEdit = () => {
    setPriceEditId(null);
    setPriceDraft("");
  };

  const savePriceEdit = async (carId) => {
    const trimmed = priceDraft.trim();
    const num = trimmed === "" ? null : Number(trimmed);
    if (trimmed !== "" && (Number.isNaN(num) || num < 0)) {
      toast({ title: "Ongeldige prijs", description: "Voer een geldig bedrag in." });
      return;
    }
    setSavingPriceId(carId);
    try {
      const updated = await carsApi.updatePrice(carId, num);
      setCars((prev) => prev.map((c) => (c.id === carId ? { ...c, price: updated.price } : c)));
      toast({ title: "Prijs aangepast" });
      cancelPriceEdit();
    } catch (e) {
      toast({ title: "Aanpassing mislukt", description: e?.response?.data?.detail || String(e) });
    } finally {
      setSavingPriceId(null);
    }
  };

  const filtered = cars.filter((c) => c.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-4 md:p-8">
      <Toaster />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5 md:mb-7">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            Stock <span className="text-[#c5a76f]">beheer</span>
          </h1>
          <p className="text-white/50 text-sm mt-1">{cars.length} wagens in stock</p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          <Link
            to="/admin/sold"
            data-testid="admin-sold-link-button"
            className="flex items-center gap-1.5 px-3 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs md:text-sm rounded transition-colors"
          >
            <CheckCircle2 className="w-4 h-4 text-[#c5a76f]" /> Verkocht
          </Link>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            data-testid="admin-sync-as24-button"
            className="flex items-center gap-2 px-3 py-2.5 md:px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs md:text-sm rounded transition-colors disabled:opacity-60"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-[#c5a76f]" />}
            <span className="hidden sm:inline">{syncing ? "Synchroniseren..." : "Sync AutoScout24"}</span>
            <span className="sm:hidden">{syncing ? "..." : "Sync"}</span>
          </button>
          <Link
            to="/admin/cars/new"
            className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-semibold tracking-widest uppercase rounded"
          >
            <Plus className="w-4 h-4" /> Nieuwe auto
          </Link>
        </div>
      </div>

      <div className="relative mb-5 md:mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op titel..."
          className="w-full pl-10 pr-4 py-2.5 bg-[#242424] border border-white/10 focus:border-[#c5a76f] outline-none rounded text-white text-sm"
        />
      </div>

      {loading ? (
        <div className="text-white/50">Laden...</div>
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="md:hidden space-y-3">
            {filtered.map((c) => (
              <div key={c.id} className="bg-[#242424] border border-white/5 rounded-md overflow-hidden">
                <div className="flex gap-3 p-3">
                  <img
                    src={imageUrl(c.images?.[0], { thumb: true })}
                    alt=""
                    className="w-24 h-20 rounded object-cover bg-[#1a1a1a] flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <a
                      href={`/listing/${c.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`admin-title-link-${c.id}`}
                      className="text-white hover:text-[#c5a76f] font-medium text-sm leading-tight block truncate"
                    >
                      {c.title}
                    </a>
                    <div className="text-xs text-white/55 mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span>{c.year}</span>
                      <span className="text-white/25">·</span>
                      <span>{formatKm(c.km)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      {priceEditId === c.id ? (
                        <div className="flex items-center gap-1 flex-1">
                          <span className="text-white/40 text-xs">€</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            autoFocus
                            value={priceDraft}
                            onChange={(e) => setPriceDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") savePriceEdit(c.id);
                              else if (e.key === "Escape") cancelPriceEdit();
                            }}
                            disabled={savingPriceId === c.id}
                            data-testid={`admin-price-input-mobile-${c.id}`}
                            className="w-24 px-2 py-1 bg-[#1a1a1a] border border-[#c5a76f]/40 text-white text-sm rounded outline-none focus:border-[#c5a76f]"
                          />
                          <button
                            onClick={() => savePriceEdit(c.id)}
                            disabled={savingPriceId === c.id}
                            className="p-1 text-green-400 active:bg-green-400/20 rounded"
                            data-testid={`admin-price-save-mobile-${c.id}`}
                          >
                            {savingPriceId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={cancelPriceEdit}
                            disabled={savingPriceId === c.id}
                            className="p-1 text-white/50 active:bg-white/10 rounded"
                            data-testid={`admin-price-cancel-mobile-${c.id}`}
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startPriceEdit(c)}
                          className="text-[#c5a76f] text-base font-semibold inline-flex items-center gap-1.5 active:opacity-70"
                          data-testid={`admin-price-edit-mobile-${c.id}`}
                        >
                          {formatPrice(c.price)}
                          <Tag className="w-3 h-3 opacity-40" />
                        </button>
                      )}
                      {c.is_upcoming ? (
                        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] rounded">Verwacht</span>
                      ) : c.reserved ? (
                        <span className="px-2 py-0.5 bg-red-500/10 text-red-400 text-[10px] rounded">Gereserveerd</span>
                      ) : c.ready ? (
                        <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-[10px] rounded">Klaar</span>
                      ) : null}
                      {c.hidden && (
                        <span className="px-2 py-0.5 bg-amber-500/15 text-amber-400 text-[10px] rounded">Verborgen</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-6 border-t border-white/5">
                  <Link
                    to={`/admin/cars/${c.id}/edit`}
                    data-testid={`admin-edit-mobile-${c.id}`}
                    className="flex items-center justify-center py-2.5 text-white/70 hover:text-[#c5a76f] active:bg-white/5"
                    aria-label="Bewerken"
                  >
                    <Edit3 className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => handleToggleHidden(c)}
                    data-testid={`admin-toggle-hidden-mobile-${c.id}`}
                    className={`flex items-center justify-center py-2.5 active:bg-white/5 ${c.hidden ? "text-amber-400" : "text-white/70 hover:text-amber-400"}`}
                    aria-label={c.hidden ? "Toon op website" : "Verberg op website"}
                  >
                    {c.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setShareCar(c)}
                    data-testid={`admin-share-mobile-${c.id}`}
                    className="flex items-center justify-center py-2.5 text-white/70 hover:text-[#c5a76f] active:bg-white/5"
                    aria-label="Deel op Instagram"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDuplicate(c.id)}
                    disabled={duplicatingId === c.id}
                    data-testid={`admin-duplicate-mobile-${c.id}`}
                    className="flex items-center justify-center py-2.5 text-white/70 hover:text-[#c5a76f] active:bg-white/5 disabled:opacity-50"
                    aria-label="Dupliceren"
                  >
                    {duplicatingId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleSold(c.id)}
                    data-testid={`admin-sold-mobile-${c.id}`}
                    className="flex items-center justify-center py-2.5 text-white/70 hover:text-green-400 active:bg-white/5"
                    aria-label="Markeer als verkocht"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(c.id)}
                    data-testid={`admin-delete-mobile-${c.id}`}
                    className="flex items-center justify-center py-2.5 text-white/70 hover:text-red-400 active:bg-white/5"
                    aria-label="Verwijderen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-10 text-white/40">Geen wagens gevonden.</div>
            )}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block bg-[#242424] border border-white/5 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#141414] text-white/50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Auto</th>
                  <th className="text-left px-5 py-3">Jaar</th>
                  <th className="text-left px-5 py-3">KM</th>
                  <th className="text-left px-5 py-3">Prijs</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-right px-5 py-3">Acties</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={imageUrl(c.images?.[0], { thumb: true })}
                          alt=""
                          className="w-14 h-10 rounded object-cover bg-[#1a1a1a]"
                        />
                        <a
                          href={`/listing/${c.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white hover:text-[#c5a76f] transition-colors"
                          title="Open publieke pagina in nieuw tabblad"
                        >
                          {c.title}
                        </a>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-white/70">{c.year}</td>
                    <td className="px-5 py-3 text-white/70">{formatKm(c.km)}</td>
                    <td className="px-5 py-3 text-[#c5a76f]" data-testid={`admin-price-cell-${c.id}`}>
                      {priceEditId === c.id ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span className="text-white/40 text-xs">€</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            autoFocus
                            value={priceDraft}
                            onChange={(e) => setPriceDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") savePriceEdit(c.id);
                              else if (e.key === "Escape") cancelPriceEdit();
                            }}
                            disabled={savingPriceId === c.id}
                            data-testid={`admin-price-input-${c.id}`}
                            className="w-28 px-2 py-1 bg-[#1a1a1a] border border-[#c5a76f]/40 text-white text-sm rounded outline-none focus:border-[#c5a76f]"
                          />
                          <button
                            onClick={() => savePriceEdit(c.id)}
                            disabled={savingPriceId === c.id}
                            title="Opslaan"
                            data-testid={`admin-price-save-${c.id}`}
                            className="p-1 text-green-400 hover:bg-green-400/10 rounded disabled:opacity-50"
                          >
                            {savingPriceId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={cancelPriceEdit}
                            disabled={savingPriceId === c.id}
                            title="Annuleren"
                            data-testid={`admin-price-cancel-${c.id}`}
                            className="p-1 text-white/50 hover:bg-white/10 rounded"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startPriceEdit(c)}
                          title="Klik om de prijs aan te passen"
                          data-testid={`admin-price-edit-${c.id}`}
                          className="inline-flex items-center gap-1.5 hover:text-[#d8bd86] transition-colors group"
                        >
                          <span>{formatPrice(c.price)}</span>
                          <Tag className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60 transition-opacity" />
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {c.is_upcoming ? (
                        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-xs rounded">Verwacht</span>
                      ) : c.reserved ? (
                        <span className="px-2 py-0.5 bg-red-500/10 text-red-400 text-xs rounded">Gereserveerd</span>
                      ) : c.ready ? (
                        <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-xs rounded">Klaar</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-white/5 text-white/40 text-xs rounded">—</span>
                      )}
                      {c.hidden && (
                        <span className="ml-1.5 px-2 py-0.5 bg-amber-500/15 text-amber-400 text-xs rounded">Verborgen</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => handleToggleHidden(c)}
                          data-testid={`admin-toggle-hidden-${c.id}`}
                          className={`p-2 transition-colors ${c.hidden ? "text-amber-400" : "text-white/60 hover:text-amber-400"}`}
                          title={c.hidden ? "Toon terug op website" : "Verberg op website"}
                        >
                          {c.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => setShareCar(c)}
                          data-testid={`admin-share-${c.id}`}
                          className="p-2 text-white/60 hover:text-[#c5a76f] transition-colors"
                          title="Deel op Instagram"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleSold(c.id)}
                          data-testid={`admin-sold-${c.id}`}
                          className="p-2 text-white/60 hover:text-green-400 transition-colors"
                          title="Markeer als verkocht"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(c.id)}
                          disabled={duplicatingId === c.id}
                          className="p-2 text-white/60 hover:text-[#c5a76f] transition-colors disabled:opacity-50"
                          title="Dupliceren"
                        >
                          {duplicatingId === c.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <Link
                          to={`/admin/cars/${c.id}/edit`}
                          className="p-2 text-white/60 hover:text-[#c5a76f] transition-colors"
                          title="Bewerken"
                        >
                          <Edit3 className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="p-2 text-white/60 hover:text-red-400 transition-colors"
                          title="Verwijderen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-5 py-10 text-center text-white/40">
                    Geen wagens gevonden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {shareCar && <ShareModal car={shareCar} onClose={() => setShareCar(null)} />}
      {soldCar && (
        <SoldModal
          car={soldCar}
          onClose={() => setSoldCar(null)}
          onDone={(res) => {
            setSoldCar(null);
            load();
            if (res?.dossier_token) {
              toast({ title: "Verkocht", description: "Leveringsdossier aangemaakt." });
            } else {
              toast({ title: "Verkocht", description: "Auto verplaatst naar verkochte wagens." });
            }
          }}
        />
      )}
    </div>
  );
}

// ---------- Instagram Share modal ----------
function ShareModal({ car, onClose }) {
  const { toast } = useToast();
  const [payload, setPayload] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const [p, img] = await Promise.all([
          axios.get(`${API_BASE}/cars/${car.id}/share-payload`, { withCredentials: true }),
          axios.get(`${API_BASE}/cars/${car.id}/story-image`, { withCredentials: true, responseType: "blob" }),
        ]);
        if (cancelled) return;
        setPayload(p.data);
        createdUrl = URL.createObjectURL(img.data);
        setImageBlobUrl(createdUrl);
      } catch (e) {
        toast({ title: "Story laden mislukt", description: e?.response?.data?.detail || String(e) });
      } finally {
        if (!cancelled) setLoadingImage(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [car.id]);

  const downloadImage = () => {
    if (!imageBlobUrl) return;
    const a = document.createElement("a");
    a.href = imageBlobUrl;
    a.download = `${(car.brand || "car").replace(/\s+/g, "-")}-${car.id.slice(0, 8)}-story.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyCaption = async () => {
    if (!payload?.caption) return;
    try {
      await navigator.clipboard.writeText(payload.caption);
      toast({ title: "Tekst gekopieerd", description: "Plak in Instagram Stories." });
    } catch {
      toast({ title: "Kopiëren mislukt", description: "Selecteer en kopieer manueel." });
    }
  };

  const copyUrl = async () => {
    if (!payload?.url) return;
    try {
      await navigator.clipboard.writeText(payload.url);
      toast({ title: "Link gekopieerd" });
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        className="bg-[#141414] border border-white/10 rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-[#c5a76f] text-xs tracking-widest uppercase">Instagram Story</div>
            <h2 className="text-white text-lg font-semibold mt-0.5">{car.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white"
            data-testid="share-modal-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[280px,1fr] gap-6 p-6">
          {/* Story preview — 1 image with both photos stacked */}
          <div className="space-y-3">
            <div className="aspect-[9/16] bg-black border border-white/10 rounded-md overflow-hidden flex items-center justify-center">
              {loadingImage ? (
                <Loader2 className="w-6 h-6 text-[#c5a76f] animate-spin" />
              ) : imageBlobUrl ? (
                <img src={imageBlobUrl} alt="Story preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-white/40 text-xs">Geen voorbeeld</div>
              )}
            </div>
            <button
              type="button"
              onClick={downloadImage}
              disabled={!imageBlobUrl}
              data-testid="share-download-button"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#c5a76f] text-black hover:bg-[#d8bd86] disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-semibold tracking-widest uppercase transition-colors"
            >
              <Download className="w-4 h-4" /> Foto downloaden
            </button>
            <AutoPostBox car={car} />
          </div>

          {/* Caption + instructions */}
          <div className="space-y-4">
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wider mb-2">Bijschrift (caption)</div>
              <textarea
                readOnly
                value={payload?.caption || ""}
                rows={9}
                data-testid="share-caption-text"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2.5 text-white/85 text-sm font-mono leading-relaxed resize-none focus:outline-none focus:border-[#c5a76f]/50"
              />
              <button
                type="button"
                onClick={copyCaption}
                disabled={!payload}
                data-testid="share-copy-caption-button"
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-white text-xs transition-colors disabled:opacity-50"
              >
                <ClipboardCopy className="w-3.5 h-3.5" /> Kopieer tekst
              </button>
            </div>
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wider mb-2">Link</div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={payload?.url || ""}
                  data-testid="share-url-text"
                  className="flex-1 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-[#c5a76f] text-xs font-mono"
                />
                <button
                  type="button"
                  onClick={copyUrl}
                  disabled={!payload}
                  data-testid="share-copy-url-button"
                  className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-white text-xs transition-colors disabled:opacity-50"
                >
                  <ClipboardCopy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="bg-[#c5a76f]/5 border border-[#c5a76f]/20 rounded p-4 text-xs text-white/75 leading-relaxed space-y-1.5">
              <div className="text-[#c5a76f] font-semibold uppercase tracking-wider text-[10px] mb-1.5">
                Zo deel je het op Instagram
              </div>
              <div>1. Klik <strong>Foto downloaden</strong> + <strong>Kopieer tekst</strong></div>
              <div>2. Open Instagram → maak een nieuw verhaal aan</div>
              <div>3. Kies de gedownloade foto uit je galerij</div>
              <div>4. Voeg een <strong>Link sticker</strong> toe met de bovenstaande URL</div>
              <div>5. Plak de tekst in een tekst-overlay of post hem in de reactie</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const IG_STATUS_LABEL = { posted: "Story geplaatst", posting: "Bezig met plaatsen…", error: "Mislukt" };

function AutoPostBox({ car }) {
  const { toast } = useToast();
  const [ig, setIg] = useState(null);
  const [state, setState] = useState(car.instagram_status || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/admin/instagram/status`, { withCredentials: true }).then((r) => setIg(r.data)).catch(() => setIg({ configured: false }));
  }, []);

  const post = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/admin/cars/${car.id}/instagram-story`, {}, { withCredentials: true });
      setState("posted");
      toast({ title: "Story geplaatst op Instagram" });
    } catch (e) {
      setState("error");
      toast({ title: "Plaatsen mislukt", description: e?.response?.data?.detail || String(e) });
    } finally {
      setBusy(false);
    }
  };

  if (!ig) return null;
  if (!ig.configured || !ig.ok) {
    return (
      <div className="text-[11px] text-white/45 leading-relaxed" data-testid="ig-auto-inactive">
        Automatisch posten {ig.configured ? `fout: ${ig.error}` : "nog niet gekoppeld"}.
      </div>
    );
  }
  return (
    <div className="border border-white/10 rounded p-3 space-y-2" data-testid="ig-auto-box">
      <div className="text-[11px] text-white/60">
        Automatisch via <span className="text-[#c5a76f]">@{ig.username}</span>
        {state && <span data-testid="ig-car-status"> · {IG_STATUS_LABEL[state] || state}</span>}
      </div>
      <button
        type="button"
        onClick={post}
        disabled={busy}
        data-testid="ig-post-now-button"
        className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-[#c5a76f]/40 text-[#c5a76f] hover:bg-[#c5a76f]/10 disabled:opacity-50 rounded text-xs font-semibold uppercase tracking-wider transition-colors"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        {state === "posted" ? "Opnieuw posten" : "Nu op Instagram posten"}
      </button>
    </div>
  );
}

function SoldModal({ car, onClose, onDone }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    sold_price: car.price != null ? String(car.price) : "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    language: "nl",
    bank_name: "",
    bank_email: "",
    insurance_name: "",
    insurance_email: "",
  });

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        create_dossier: true,
        sold_price: f.sold_price === "" ? null : Number(f.sold_price),
        language: f.language,
        customer_name: f.customer_name.trim() || null,
        customer_email: f.customer_email.trim() || null,
        customer_phone: f.customer_phone.trim() || null,
        bank: f.bank_name || f.bank_email ? { name: f.bank_name.trim() || null, email: f.bank_email.trim() || null } : null,
        insurance:
          f.insurance_name || f.insurance_email
            ? { name: f.insurance_name.trim() || null, email: f.insurance_email.trim() || null }
            : null,
      };
      const res = await carsApi.markSold(car.id, body);
      onDone(res);
    } catch (e) {
      toast({ title: "Fout", description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  const Inp = ({ label, value, onChange, type = "text", placeholder, testId }) => (
    <label className="block">
      <span className="text-white/50 text-xs">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-1 w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#c5a76f]/50"
      />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="sold-modal">
      <div className="bg-[#141414] border border-white/10 rounded-lg w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-[#c5a76f] text-xs tracking-widest uppercase">Markeer als verkocht</div>
            <h2 className="text-white text-lg font-semibold mt-0.5">{car.title}</h2>
          </div>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-white" data-testid="sold-modal-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-white/55 text-sm">
            Er wordt automatisch een <b className="text-white/80">leveringsdossier</b> aangemaakt met een opvolglink voor de klant.
            Vul de klantgegevens in zodat de klant meldingen krijgt.
          </p>
          <div className="border border-[#c5a76f]/30 bg-[#c5a76f]/5 rounded p-3">
            {Inp({ label: "Echte verkoopprijs (€) — gebruikt in het winstoverzicht", value: f.sold_price, onChange: (v) => setF({ ...f, sold_price: v }), type: "number", placeholder: "Bv. 28500", testId: "sold-price-input" })}
            <p className="text-white/40 text-[11px] mt-1.5">Vooraf ingevuld met de vraagprijs. Pas aan naar het bedrag waarvoor de wagen effectief verkocht is.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Inp({ label: "Klantnaam", value: f.customer_name, onChange: (v) => setF({ ...f, customer_name: v }), placeholder: "Voornaam Naam", testId: "sold-customer-name" })}
            {Inp({ label: "Klant e-mail", value: f.customer_email, onChange: (v) => setF({ ...f, customer_email: v }), type: "email", placeholder: "klant@email.be", testId: "sold-customer-email" })}
            {Inp({ label: "Klant telefoon", value: f.customer_phone, onChange: (v) => setF({ ...f, customer_phone: v }), placeholder: "+32 ...", testId: "sold-customer-phone" })}
            <label className="block">
              <span className="text-white/50 text-xs">Taal van de klant</span>
              <select
                value={f.language}
                onChange={(e) => setF({ ...f, language: e.target.value })}
                data-testid="sold-customer-language"
                className="mt-1 w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c5a76f]/50"
              >
                <option value="nl">Nederlands</option>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </label>
          </div>
          <details className="bg-white/[0.03] border border-white/10 rounded p-3">
            <summary className="text-white/70 text-sm cursor-pointer select-none">Bank / verzekering (optioneel)</summary>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {Inp({ label: "Bank — contactnaam", value: f.bank_name, onChange: (v) => setF({ ...f, bank_name: v }), placeholder: "Naam" })}
              {Inp({ label: "Bank — e-mail", value: f.bank_email, onChange: (v) => setF({ ...f, bank_email: v }), type: "email", placeholder: "bank@email.be" })}
              {Inp({ label: "Verzekering — contactnaam", value: f.insurance_name, onChange: (v) => setF({ ...f, insurance_name: v }), placeholder: "Naam" })}
              {Inp({ label: "Verzekering — e-mail", value: f.insurance_email, onChange: (v) => setF({ ...f, insurance_email: v }), type: "email", placeholder: "makelaar@email.be" })}
            </div>
          </details>
          <div className="flex items-center justify-end gap-3 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-white/60 hover:text-white text-sm" data-testid="sold-cancel">
              Annuleren
            </button>
            <button
              onClick={submit}
              disabled={saving}
              data-testid="sold-confirm"
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded text-sm font-medium disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Verkocht + dossier aanmaken
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
